// @flow

import React, { PureComponent } from 'react';
import type { Props } from 'react';
import { debounce, get } from 'lodash';
import { Provider } from 'react-redux';
import { bindActionCreators } from 'redux';
import { clientEnhance, getDeviceType, setDeviceTypeConfig } from '@fil-global/eiswagen';
import { LocalizeProvider } from 'react-localize-redux';
import { RouteRenderer } from './routeRenderer';

import routes from './constants/routes';
import { onDeviceResize } from './global/common/actions';
import { selectDeviceType } from './global/common/selectors';
import { userLoggedOut } from './global/userAuth/actions';
import routeConfig from './routes';
import './styles/styles.scss';
import { setupHttpClientInterceptor } from './utils/httpClient';
import { DEVICE_TYPE_CONFIG } from './global/constants';
import { languageInit } from './utils/translateUtil';
import { selectPermission } from './global/fccCommon/selectors';

class App extends PureComponent<Props> {
  constructor(props) {
    super(props);
    setDeviceTypeConfig(DEVICE_TYPE_CONFIG);
    setupHttpClientInterceptor(this.httpResponse401Handler, this.httpResponse500Handler);
  }

  setDeviceConfig = debounce(() => {
    const { deviceType, onDeviceResizeAction } = this.props;

    const currentDeviceType = getDeviceType();

    if (deviceType && currentDeviceType !== deviceType) {
      onDeviceResizeAction({
        deviceType: currentDeviceType,
      });
    }
  }, 250);

  httpResponse401Handler = error => {
    const { handleUserLogout, history, isAuthenticated } = this.props;
    if (isAuthenticated) {
      history.push(routes.LOGIN);
      handleUserLogout(error);
    }
  };

  /* eslint-disable */
  httpResponse500Handler = () => {
    const { history } = this.props;
    history.push(routes.ERROR_PAGE);
  };

  componentDidMount = () => {
    window.addEventListener('resize', this.setDeviceConfig);
  };

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.setDeviceConfig);
  };

  render() {
    const { store, location, sessionCheckDone, isAuthenticated, language } = this.props;

    return (
      sessionCheckDone && (
        <Provider store={store}>
          <LocalizeProvider initialize={languageInit(language)}>
            <RouteRenderer
              location={location}
              routeConfig={routeConfig}
              store={store}
              isSecureEnabled={isAuthenticated}
            />
          </LocalizeProvider>
        </Provider>
      )
    );
  }
}

const mapStateToProps = state => ({
  deviceType: selectDeviceType(state),
  sessionCheckDone: get(state, 'global.userAuth.sessionCheckDone', false),
  isAuthenticated: get(state, 'global.userAuth.isAuthenticated', false),
  language: get(selectPermission(state), 'language', null),
});

const mapDispatchToProps = dispatch => ({
  onDeviceResizeAction: bindActionCreators(onDeviceResize, dispatch),
  handleUserLogout: bindActionCreators(userLoggedOut, dispatch),
});

export default clientEnhance(App, {
  key: 'app',
  mapStateToProps,
  mapDispatchToProps,
});
